export const ADOBE_MC_PARAM = 'adobe_mc';

// Adobe CJA cross-domain parameter, e.g. TS=1785730377|MCMID=5031...|MCORGID=0517...@AdobeOrg
const adobeMcRegex = /^[A-Za-z0-9_]+=[A-Za-z0-9_.@-]+(\|[A-Za-z0-9_]+=[A-Za-z0-9_.@-]+)*$/;
const maxAdobeMcLength = 512;

/**
 * Checks if an adobe_mc value is well formed (pipe separated KEY=VALUE pairs).
 * @param adobeMc The untrusted (decoded) adobe_mc value.
 * @returns true if the value is safe to pass through, false otherwise.
 */
export function isValidAdobeMc(adobeMc: string): boolean {
  return adobeMc.length <= maxAdobeMcLength && adobeMcRegex.test(adobeMc);
}

/**
 * Appends the adobe_mc parameter to a URL so Adobe CJA can continue tracking the user's journey.
 * Existing query parameters are left exactly as they are. If the URL already has an adobe_mc
 * parameter, or the value is missing or malformed, the URL is returned unchanged.
 * @param urlString The (already validated) URL to append to.
 * @param adobeMc The untrusted (decoded) adobe_mc value.
 * @returns The URL with adobe_mc appended.
 */
export function appendAdobeMc(urlString: string, adobeMc: string | null | undefined): string {
  if (!adobeMc || !isValidAdobeMc(adobeMc)) {
    return urlString;
  }
  try {
    const url = new URL(urlString);
    if (url.searchParams.has(ADOBE_MC_PARAM)) {
      return urlString;
    }
    // Append to the raw query string rather than using searchParams.set, which would re-encode
    // the existing parameters (e.g. CourseYear=CurrentYear%2b1 and f=$SST.MAKEANAPP.WIZ)
    const param = `${ADOBE_MC_PARAM}=${encodeURIComponent(adobeMc)}`;
    url.search = url.search ? `${url.search}&${param}` : `?${param}`;
    return url.toString();
  } catch {
    return urlString;
  }
}
